import RcDomainObject from "../../../common/RcDomainObject";
import Company from "../../company/Company.js";
import Vehicle from "../../vehicle/Vehicle.js";
import StringUtils from "../../../../utils/StringUtils.js";
import RcTime from "../../../session/time/RcTime.js";
import GpsPosition from "../position/GpsPosition.js";

export default class GpsDevice extends RcDomainObject {

  static MODEL_NAME = "GpsDevice";

  static FIELDS = {
    IMEI: "imei",
    ALLOW_CONNECTION: "allowConnection",
    COMPANY: "Company",
    VEHICLE: "Vehicle",
    SIM_NUMBER: "simNumber",
    LANGUAGE: "language",
    LAST_UPDATE: "lastUpdate",
    LAST_LAT: "lastLat",
    LAST_LON: "lastLon",
    CURRENT_IGNITION: "currentIgnition",
    CURRENT_BATTERY_LEVEL: "currentBatteryLevel",
    CURRENT_GSM_SIGNAL: "currentGsmSignal",
    CURRENT_CHARGING: "currentCharging",
    CURRENT_STATUS: "currentStatus",
    TYPE: "type",
  }

  static VALUES = {
    Type: {
      OBD2: "obd2",
      WIRED: "wired",
      BATTERY: "battery"
    }
  }

  constructor(domain, data) {
    super(domain, data, GpsDevice.MODEL_NAME);
  }

  fields() {
    return GpsDevice.FIELDS;
  }

  imei() {
    return this.getString(GpsDevice.FIELDS.IMEI);
  }

  withImei(imei) {
    this.put(GpsDevice.FIELDS.IMEI, imei);
    return this;
  }

  allowConnection() {
    return this.getBoolean(GpsDevice.FIELDS.ALLOW_CONNECTION);
  }

  withAllowConnection(allowConnection) {
    this.put(GpsDevice.FIELDS.ALLOW_CONNECTION, allowConnection);
    return this;
  }

  company() {
    return new Company(this.domain, this.get(GpsDevice.FIELDS.COMPANY));
  }

  withCompany(company) {
    this.put(GpsDevice.FIELDS.COMPANY, company);
    return this;
  }

  vehicle() {
    return new Vehicle(this.domain, this.get(GpsDevice.FIELDS.VEHICLE));
  }

  withVehicle(vehicle) {
    this.put(GpsDevice.FIELDS.VEHICLE, vehicle);
    return this;
  }

  simNumber() {
    return this.getString(GpsDevice.FIELDS.SIM_NUMBER);
  }

  withSimNumber(simNumber) {
    this.put(GpsDevice.FIELDS.SIM_NUMBER, simNumber);
    return this;
  }

  language() {
    return this.getString(GpsDevice.FIELDS.LANGUAGE);
  }

  withLanguage(language) {
    this.put(GpsDevice.FIELDS.LANGUAGE, language);
    return this;
  }

  lastUpdate() {
    return this.getString(GpsDevice.FIELDS.LAST_UPDATE);
  }

  lastUpdateDisplay() {
    let time = this.lastUpdate();
    if (time <= 0) {
      return 'No update received';
    }
    let rcTime = RcTime.time();
    let lastUpdate = rcTime.at(time);
    return lastUpdate.displayAs_YYYY_MM_DD_HH_MM_SS_A();
  }

  withLastUpdate(lastUpdate) {
    this.put(GpsDevice.FIELDS.LAST_UPDATE, lastUpdate);
    return this;
  }

  lastLat() {
    return this.getDouble(GpsDevice.FIELDS.LAST_LAT);
  }

  withLastLat(lastLat) {
    this.put(GpsDevice.FIELDS.LAST_LAT, lastLat);
    return this;
  }

  lastLon() {
    return this.getDouble(GpsDevice.FIELDS.LAST_LON);
  }

  withLastLon(lastLon) {
    this.put(GpsDevice.FIELDS.LAST_LON, lastLon);
    return this;
  }

  currentIgnition() {
    return this.getBoolean(GpsDevice.FIELDS.CURRENT_IGNITION);
  }

  withCurrentIgnition(currentIgnition) {
    this.put(GpsDevice.FIELDS.CURRENT_IGNITION, currentIgnition);
    return this;
  }

  currentBatteryLevel() {
    return this.getInt(GpsDevice.FIELDS.CURRENT_BATTERY_LEVEL);
  }

  withCurrentBatteryLevel(currentBatteryLevel) {
    this.put(GpsDevice.FIELDS.CURRENT_BATTERY_LEVEL, currentBatteryLevel);
    return this;
  }

  currentGsmSignal() {
    return this.getInt(GpsDevice.FIELDS.CURRENT_GSM_SIGNAL);
  }

  withCurrentGsmSignal(currentGsmSignal) {
    this.put(GpsDevice.FIELDS.CURRENT_GSM_SIGNAL, currentGsmSignal);
    return this;
  }

  currentCharging() {
    return this.getBoolean(GpsDevice.FIELDS.CURRENT_CHARGING);
  }

  withCurrentCharging(currentCharging) {
    this.put(GpsDevice.FIELDS.CURRENT_CHARGING, currentCharging);
    return this;
  }

  currentStatus() {
    return this.getString(GpsDevice.FIELDS.CURRENT_STATUS);
  }

  withCurrentStatus(currentStatus) {
    this.put(GpsDevice.FIELDS.CURRENT_STATUS, currentStatus);
    return this;
  }

  type() {
    return this.getString(GpsDevice.FIELDS.TYPE);
  }

  currentGsmSignalDisplay() {
    switch (this.currentGsmSignal()) {
      case 0:
        return "No Signal";
      case 1:
        return "Extremely Weak";
      case 2:
        return "Very Weak";
      case 3:
        return "Good";
      case 4:
        return "Excellent";
      default:
        return "";
    }
  }

  currentBatteryLevelDisplay() {
    switch (this.currentBatteryLevel()) {
      case 0:
        return "No Power (Shutdown)";
      case 1:
        return "Extremely Low";
      case 2:
        return "Very Low";
      case 3:
        return "Low";
      case 4:
        return "Medium";
      case 5:
        return "High";
      case 6:
        return "Full";
      default:
        return "";
    }
  }

  lastPosition() {
    let positionMap = this.domain.gpsPositions().findByGpsDevice(this);
    if (positionMap.isNotEmpty()) {
      let list = positionMap.sort();
      return list[0];
    }
    return new GpsPosition(this.domain).withLatitude(this.lastLat()).withLongitude(this.lastLon()).withDeviceTime(this.lastUpdate());
  }

  lastRecordedSpeedDisplay(showUnits = true) {
    let positionMap = this.domain.gpsPositions().findByGpsDevice(this);
    if (positionMap.isNotEmpty()) {
      let list = positionMap.sort();
      let speed = ((list[0]?.speed() ?? 0)).toFixed(1);
      return StringUtils.trim(StringUtils.format("{0} {1}", [speed, showUnits ? 'Km/h' : '']));
    }
    return StringUtils.trim(StringUtils.format("{0} {1}", ["0.0", showUnits ? 'Km/h' : '']));
  }

  lastRecordedSpeed() {
    let positionMap = this.domain.gpsPositions().findByGpsDevice(this);
    if (positionMap.isNotEmpty()) {
      let list = positionMap.sort();
      let speed = ((list[0]?.speed() ?? 0)).toFixed(1);
      return speed;
    }
    return 0;
  }
}
